import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { ExtraWorkOrdersApiAdapter } from '../adapters';
import {
  CreateExtraWorkOrderRequest,
  RegisterWasteRequest,
  CompleteLineRequest,
  ExtraWorkOrder,
} from '../models';
import { translateApiError } from '../utils/errorTranslator';

export const useCreateExtraWorkOrder = () => {
  const { toast } = useToast();
  const { t } = useTranslation('extraWorkOrdersModule');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateExtraWorkOrderRequest) => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ExtraWorkOrdersApiAdapter(httpClient);
      return adapter.createExtraWorkOrder(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['extra-work-orders'] });
      toast({
        title: t('toasts.createSuccess.title', 'Orden creada'),
        description:
          response.ref ||
          t('toasts.createSuccess.description', 'La orden se ha creado correctamente'),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('toasts.error.unexpected', 'Error inesperado');
      toast({
        title: t('toasts.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useStartLine = () => {
  const { toast } = useToast();
  const { t } = useTranslation('extraWorkOrdersModule');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      lineId,
    }: {
      orderId: number;
      lineId: number;
    }) => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ExtraWorkOrdersApiAdapter(httpClient);
      return adapter.startLine(orderId, lineId);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['extra-work-order'] });
      queryClient.invalidateQueries({ queryKey: ['extra-work-orders'] });
      toast({
        title: t('toasts.startLineSuccess.title', 'Línea iniciada'),
        description:
          response.message ||
          t('toasts.startLineSuccess.description', 'La línea se ha iniciado correctamente'),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('toasts.error.unexpected', 'Error inesperado');
      toast({
        title: t('toasts.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useCompleteLine = () => {
  const { toast } = useToast();
  const { t } = useTranslation('extraWorkOrdersModule');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      lineId,
      data,
    }: {
      orderId: number;
      lineId: number;
      data: CompleteLineRequest;
    }) => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ExtraWorkOrdersApiAdapter(httpClient);
      return adapter.completeLine(orderId, lineId, data);
    },
    onSuccess: (response) => {
      // En modo real, invalidar queries para refrescar desde la API
      queryClient.invalidateQueries({ queryKey: ['extra-work-order'] });
      queryClient.invalidateQueries({ queryKey: ['extra-work-orders'] });
      
      toast({
        title: t('toasts.completeLineSuccess.title', 'Línea completada'),
        description:
          response.message ||
          t('toasts.completeLineSuccess.description', 'La línea se ha completado correctamente'),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      const errorMessage = translateApiError(error, t);
      toast({
        title: t('toasts.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useRegisterWaste = () => {
  const { toast } = useToast();
  const { t } = useTranslation('extraWorkOrdersModule');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      lineId,
      data,
    }: {
      orderId: number;
      lineId: number;
      data: RegisterWasteRequest;
    }) => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ExtraWorkOrdersApiAdapter(httpClient);
      return adapter.registerWaste(orderId, lineId, data);
    },
    onSuccess: (response) => {
      // En modo real, invalidar queries para refrescar desde la API
      queryClient.invalidateQueries({ queryKey: ['extra-work-order'] });
      queryClient.invalidateQueries({ queryKey: ['extra-work-orders'] });
      
      toast({
        title: t('toasts.registerWasteSuccess.title', 'Merma registrada'),
        description:
          response.message ||
          t('toasts.registerWasteSuccess.description', 'La merma se ha registrado correctamente'),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('toasts.error.unexpected', 'Error inesperado');
      toast({
        title: t('toasts.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};
